import { useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { useRowDetail } from "../RowDetailSheet";
import { EvidenceBadge } from "../EvidenceBadge";
import { useStoreNavigation } from "../storeNavigation";
import { SeeMoreSection, type SeeMoreRange } from "../SeeMoreSection";
import type { FlatSection, RowDetail } from "../types";
import { cn } from "@/shared/lib/utils";

interface SectionTableProps {
  title: string;
  columns: string[];
  rows: (string | number)[][];
  rowDetail?: RowDetail;
  evidencePerRow?: (string[] | null | undefined)[];
  /** Same length as rows[]; a value gives the row click-through to the
   *  per-store view (wins over rowDetail when both are present). */
  storeNumberPerRow?: (string | null | undefined)[];
  /** Opt-in see-more: constant visible head + count line + one-tap reveal-all. */
  head?: number;
  /** Count-line noun for the see-more count line. Default "rows". */
  noun?: string;
  /** Per-column alignment, indexed in the given column order. Omitted → all
   *  left (existing callers unchanged). "right" adds text-right to the header
   *  and body cells — the intel-chat card uses it for numeric columns. */
  columnAlign?: ("left" | "right")[];
  /** Rendered instead of the table when rows is empty. */
  emptyState?: string;
}

export function SectionTable({
  title,
  columns,
  rows,
  rowDetail,
  evidencePerRow,
  storeNumberPerRow,
  head,
  noun,
  columnAlign,
  emptyState,
}: SectionTableProps) {
  const detail = useRowDetail();
  const nav = useStoreNavigation();
  const lastTriggerRef = useRef<HTMLTableRowElement | null>(null);

  const openDetail = (row: (string | number)[], el: HTMLTableRowElement) => {
    if (!rowDetail) return;
    lastTriggerRef.current = el;
    const titleIdx = Math.max(0, Math.min(rowDetail.title_column_index, row.length - 1));
    const title = String(row[titleIdx] ?? "Detail");
    const rendered: FlatSection[] = rowDetail.sections.map((s) => withRowContext(s, columns, row));
    detail.open({ title, sections: rendered });
  };

  const hasEvidence = !!evidencePerRow && evidencePerRow.some((e) => e && e.length > 0);

  if (rows.length === 0 && emptyState) {
    return <p className="px-5 py-4 text-sm text-muted-foreground">{emptyState}</p>;
  }

  const renderRow = (row: (string | number)[], ri: number) => {
    const urls = evidencePerRow?.[ri];
    // Store click-through wins over rowDetail when a row carries both.
    const storeNumber = storeNumberPerRow?.[ri];
    const openStore =
      nav && storeNumber ? () => nav.openStore(storeNumber, String(row[0] ?? "")) : null;
    const clickableRow = !!openStore || !!rowDetail;
    const activate = (el: HTMLTableRowElement) => {
      if (openStore) openStore();
      else openDetail(row, el);
    };
    return (
      <TableRow
        key={ri}
        className={cn(clickableRow && "cursor-pointer")}
        onClick={(e) => activate(e.currentTarget)}
        tabIndex={clickableRow ? 0 : undefined}
        onKeyDown={(e) => {
          if (!clickableRow) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            activate(e.currentTarget);
          }
        }}
      >
        {row.map((cell, ci) => (
          <TableCell
            key={ci}
            className={cn("tabular-nums", columnAlign?.[ci] === "right" && "text-right")}
          >
            {cell}
          </TableCell>
        ))}
        {hasEvidence ? (
          <TableCell className="text-right">
            {urls && urls.length > 0 ? (
              <EvidenceBadge
                urls={urls}
                caption={{
                  // Prefer the explicit title column from row_detail
                  // (the canonical row identifier the model picked);
                  // fall back to the first column. Using the LAST
                  // column was a guess that mis-captioned in
                  // practice.
                  leaf: String(
                    (rowDetail
                      ? row[
                          Math.max(
                            0,
                            Math.min(
                              rowDetail.title_column_index,
                              row.length - 1,
                            ),
                          )
                        ]
                      : row[0]) ?? "Row",
                  ),
                }}
                size="sm"
              />
            ) : null}
          </TableCell>
        ) : null}
      </TableRow>
    );
  };

  const header = (
    <TableHeader>
      <TableRow>
        {columns.map((c, ci) => (
          <TableHead key={c} className={cn(columnAlign?.[ci] === "right" && "text-right")}>
            {c}
          </TableHead>
        ))}
        {hasEvidence ? <TableHead className="w-12 text-right">Evidence</TableHead> : null}
      </TableRow>
    </TableHeader>
  );

  if (head != null) {
    // See-more splits the rows across an animated overflow container, which a
    // single <tbody> can't span — each slice is its own table. `table-fixed`
    // plus a shared <colgroup> keeps the two tables' columns aligned.
    const colgroup = (
      <colgroup>
        {columns.map((c) => (
          <col key={c} />
        ))}
        {hasEvidence ? <col className="w-12" /> : null}
      </colgroup>
    );
    const sliceTable = ({ start, end }: SeeMoreRange) => (
      <Table className="table-fixed">
        <caption className="sr-only">{start === 0 ? title : `${title} (continued)`}</caption>
        {colgroup}
        {start === 0 ? header : null}
        <TableBody>{rows.slice(start, end).map((row, i) => renderRow(row, start + i))}</TableBody>
      </Table>
    );
    return (
      <SeeMoreSection head={head} total={rows.length} noun={noun ?? "rows"}>
        {sliceTable}
      </SeeMoreSection>
    );
  }

  return (
    <Table>
      {/* The visible title lives in the SectionShell header; the caption names
          the table itself for AT (the prop used to be silently discarded). */}
      <caption className="sr-only">{title}</caption>
      {header}
      <TableBody>{rows.map(renderRow)}</TableBody>
    </Table>
  );
}

/**
 * Minimal placeholder substitution for {{column_name}} tokens in nested
 * markdown bodies — the live skill should emit fully-resolved sections, this
 * is a tolerance backstop.
 */
function withRowContext(
  section: FlatSection,
  columns: string[],
  row: (string | number)[],
): FlatSection {
  if (section.kind !== "markdown") return section;
  const ctx: Record<string, string> = {};
  columns.forEach((c, i) => {
    ctx[c] = String(row[i] ?? "");
  });
  const body = section.body.replace(/\{\{(.+?)\}\}/g, (_, k) => ctx[k.trim()] ?? "");
  return { ...section, body };
}
